import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import Forward10Icon from '@mui/icons-material/Forward10';
import Replay10Icon from '@mui/icons-material/Replay10';
import { getVideoPoster, getVideoPreviewFrame } from '../utils/imageTransform';

interface VideoPlayerProps {
  src: string;
  /** Captured at upload; lets the scrub bar render before metadata loads. */
  duration?: number;
  maxHeight?: string | number;
  /** Fill the container instead of sizing to the video (lightbox). */
  contain?: boolean;
  rounded?: boolean;
  autoPlay?: boolean;
}

const SPEEDS = [0.5, 1, 1.5, 2];
const SEEK_STEP = 10;
const PREVIEW_WIDTH = 160;

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/** Warm the HTTP cache for a preview frame so scrubbing doesn't flicker. */
const preloadedFrames = new Set<string>();
const preloadFrame = (url: string) => {
  if (preloadedFrames.has(url)) return;
  preloadedFrames.add(url);
  const img = new Image();
  img.src = url;
};

const VideoPlayer: FC<VideoPlayerProps> = ({
  src,
  duration: durationProp,
  maxHeight,
  contain = false,
  rounded = true,
  autoPlay = false,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(autoPlay);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationProp ?? 0);
  // Feed videos start muted: unexpected audio while scrolling is hostile, and
  // browsers block unmuted programmatic playback anyway.
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [scrub, setScrub] = useState<{ time: number; x: number } | null>(null);

  const poster = getVideoPoster(src, durationProp);

  const seekTo = useCallback(
    (time: number) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(time)) return;
      const clamped = Math.max(0, Math.min(time, duration || video.duration || 0));
      video.currentTime = clamped;
      setCurrentTime(clamped);
    },
    [duration]
  );

  const seekBy = useCallback(
    (delta: number) => seekTo((videoRef.current?.currentTime ?? 0) + delta),
    [seekTo]
  );

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video
        .play()
        .then(() => setHasStarted(true))
        .catch(() => {
          // Rejected by autoplay policy, or aborted by a still-pending load.
          // Keep the poster and the play affordance up rather than hiding them
          // over a frame that isn't playing.
          setIsPlaying(false);
          setHasStarted(false);
        });
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const next = !video.muted;
    video.muted = next;
    setIsMuted(next);
    // Unmuting at zero volume would look broken.
    if (!next && video.volume === 0) {
      video.volume = 1;
      setVolume(1);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    // Fullscreen the container, not the <video> — otherwise the browser shows
    // its own native controls and our control bar disappears.
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.().catch(() => {});
    }
  }, []);

  const cycleSpeed = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    video.playbackRate = next;
    setSpeed(next);
  }, [speed]);

  useEffect(() => {
    const onFullscreenChange = () =>
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // --- scrubbing ------------------------------------------------------------
  // Genuinely JS: mapping a pointer position to a timestamp is gesture work.

  const timeFromPointer = useCallback(
    (clientX: number): { time: number; x: number } | null => {
      const track = trackRef.current;
      if (!track || !duration) return null;
      const rect = track.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      return { time: (x / rect.width) * duration, x };
    },
    [duration]
  );

  const onTrackPointerMove = (e: React.PointerEvent) => {
    const next = timeFromPointer(e.clientX);
    if (!next) return;
    setScrub(next);
    const frame = getVideoPreviewFrame(src, next.time, PREVIEW_WIDTH);
    if (frame) preloadFrame(frame);
    // Dragging (pointer captured) scrubs the video itself.
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      seekTo(next.time);
    }
  };

  const onTrackPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    const next = timeFromPointer(e.clientX);
    if (next) {
      setScrub(next);
      seekTo(next.time);
    }
  };

  const onTrackPointerUp = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    // Touch has no hover state, so the preview must go when the finger lifts.
    if (e.pointerType !== 'mouse') setScrub(null);
  };

  // --- tap handling ---------------------------------------------------------

  const lastTap = useRef<{ t: number; zone: string } | null>(null);
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onSurfacePointerUp = (e: React.PointerEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const zone = x < rect.width / 3 ? 'left' : x > (rect.width * 2) / 3 ? 'right' : 'center';

    // A mouse gets an immediate toggle; delaying it to wait for a double-click
    // would feel broken. Desktop seeks with arrow keys instead.
    if (e.pointerType === 'mouse') {
      togglePlay();
      return;
    }

    const now = Date.now();
    if (lastTap.current && now - lastTap.current.t < 300 && lastTap.current.zone === zone) {
      if (tapTimer.current) clearTimeout(tapTimer.current);
      lastTap.current = null;
      if (zone === 'left') seekBy(-SEEK_STEP);
      else if (zone === 'right') seekBy(SEEK_STEP);
      else togglePlay();
      return;
    }

    lastTap.current = { t: now, zone };
    tapTimer.current = setTimeout(() => {
      togglePlay();
      lastTap.current = null;
    }, 300);
  };

  useEffect(() => () => {
    if (tapTimer.current) clearTimeout(tapTimer.current);
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const handlers: Record<string, () => void> = {
      ' ': togglePlay,
      k: togglePlay,
      ArrowLeft: () => seekBy(-5),
      ArrowRight: () => seekBy(5),
      j: () => seekBy(-SEEK_STEP),
      l: () => seekBy(SEEK_STEP),
      m: toggleMute,
      f: toggleFullscreen,
    };
    const handler = handlers[e.key];
    if (handler) {
      e.preventDefault();
      handler();
    }
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;
  const scrubFrame = scrub ? getVideoPreviewFrame(src, scrub.time, PREVIEW_WIDTH) : null;

  const controlBtnSx = {
    color: '#fff',
    padding: '6px',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
  } as const;

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      sx={{
        position: 'relative',
        width: '100%',
        height: contain || isFullscreen ? '100%' : 'auto',
        backgroundColor: '#000',
        borderRadius: rounded && !isFullscreen ? 'var(--sh-lg)' : 0,
        overflow: 'hidden',
        outline: 'none',
        '&:focus-visible': { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
        // Reveal the controls on hover, keyboard focus, or while paused. The
        // :paused pseudo-class isn't supported in the Android WebView, so the
        // paused state comes from a data attribute instead.
        '&:hover .vp-controls, &:focus-within .vp-controls, &[data-paused="true"] .vp-controls':
          { opacity: 1, transform: 'translateY(0)' },
      }}
      data-paused={!isPlaying}
    >
      <Box
        component="video"
        ref={videoRef}
        src={src}
        poster={poster ?? undefined}
        autoPlay={autoPlay}
        muted={isMuted}
        playsInline
        preload="metadata"
        // No native control bar: it carries the download and PiP entries we're
        // removing, and can't be styled. These are belt-and-braces for the
        // context menu, which still exposes "Save video as".
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
        onPointerUp={onSurfacePointerUp}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={(e: React.SyntheticEvent<HTMLVideoElement>) =>
          setCurrentTime(e.currentTarget.currentTime)
        }
        onLoadedMetadata={(e: React.SyntheticEvent<HTMLVideoElement>) => {
          // Trust the real metadata over the value stored at upload.
          if (Number.isFinite(e.currentTarget.duration)) {
            setDuration(e.currentTarget.duration);
          }
        }}
        onEnded={() => setIsPlaying(false)}
        sx={{
          display: 'block',
          width: '100%',
          height: contain || isFullscreen ? '100%' : 'auto',
          maxHeight: isFullscreen ? 'none' : maxHeight,
          objectFit: contain || isFullscreen ? 'contain' : 'cover',
          cursor: 'pointer',
        }}
      />

      {/* Big play affordance over the poster, before first playback. */}
      {!hasStarted && (
        <IconButton
          onClick={togglePlay}
          aria-label="Play video"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: theme.tokens.colors.scrim,
            backdropFilter: theme.tokens.effects.glassBlur,
            WebkitBackdropFilter: theme.tokens.effects.glassBlur,
            color: '#fff',
            width: 64,
            height: 64,
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
          }}
        >
          <PlayArrowIcon sx={{ fontSize: 36 }} />
        </IconButton>
      )}

      <Box
        className="vp-controls"
        onPointerUp={(e: React.PointerEvent) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          insetInline: 0,
          insetBlockEnd: 0,
          paddingInline: 1,
          paddingBlockEnd: isFullscreen ? 'env(safe-area-inset-bottom, 8px)' : '4px',
          paddingBlockStart: 3,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
          opacity: 0,
          transform: 'translateY(8px)',
          transition: `opacity ${theme.tokens.animations.durM3} ${theme.tokens.animations.easeM3}, transform ${theme.tokens.animations.durM3} ${theme.tokens.animations.easeM3}`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {/* scrub track */}
        <Box
          ref={trackRef}
          onPointerDown={onTrackPointerDown}
          onPointerMove={onTrackPointerMove}
          onPointerUp={onTrackPointerUp}
          onPointerLeave={() => setScrub(null)}
          sx={{
            position: 'relative',
            height: 16,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            touchAction: 'none',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 4,
              borderRadius: 'var(--radius-pill)',
              backgroundColor: 'rgba(255,255,255,0.3)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                insetBlock: 0,
                insetInlineStart: 0,
                width: `${progress}%`,
                borderRadius: 'var(--radius-pill)',
                backgroundColor: theme.palette.primary.main,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                insetBlockStart: '50%',
                insetInlineStart: `${progress}%`,
                transform: 'translate(-50%, -50%)',
                width: 12,
                height: 12,
                borderRadius: 'var(--radius-circle)',
                backgroundColor: theme.palette.primary.main,
              }}
            />
          </Box>

          {/* hover / drag preview */}
          {scrub && scrubFrame && (
            <Box
              sx={{
                position: 'absolute',
                insetBlockEnd: 24,
                insetInlineStart: scrub.x,
                transform: 'translateX(-50%)',
                pointerEvents: 'none',
                borderRadius: 'var(--radius-image)',
                overflow: 'hidden',
                border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.glassLine}`,
                backgroundColor: '#000',
                boxShadow: theme.shadows[6],
              }}
            >
              <Box
                component="img"
                src={scrubFrame}
                alt=""
                sx={{ display: 'block', width: PREVIEW_WIDTH, height: 'auto' }}
              />
              <Typography
                sx={{
                  position: 'absolute',
                  insetBlockEnd: 2,
                  insetInlineStart: '50%',
                  transform: 'translateX(-50%)',
                  fontFamily: theme.tokens.typography.monoFontFamily,
                  fontSize: '10px',
                  color: '#fff',
                  textShadow: '0 1px 2px rgba(0,0,0,0.9)',
                }}
              >
                {formatTime(scrub.time)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* button row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            size="small"
            sx={controlBtnSx}
          >
            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>

          <IconButton
            onClick={() => seekBy(-SEEK_STEP)}
            aria-label="Back 10 seconds"
            size="small"
            sx={controlBtnSx}
          >
            <Replay10Icon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => seekBy(SEEK_STEP)}
            aria-label="Forward 10 seconds"
            size="small"
            sx={controlBtnSx}
          >
            <Forward10Icon fontSize="small" />
          </IconButton>

          <IconButton
            onClick={toggleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            size="small"
            sx={controlBtnSx}
          >
            {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
          </IconButton>

          {/* iOS ignores programmatic volume entirely (hardware buttons only),
              so the slider is only offered where a pointer implies it works. */}
          <Box
            sx={{
              display: 'none',
              '@media (pointer: fine)': { display: 'block' },
              width: 72,
              paddingInline: 0.5,
            }}
          >
            <Slider
              size="small"
              min={0}
              max={1}
              step={0.05}
              value={isMuted ? 0 : volume}
              aria-label="Volume"
              onChange={(_, value) => {
                const next = value as number;
                const video = videoRef.current;
                if (!video) return;
                video.volume = next;
                video.muted = next === 0;
                setVolume(next);
                setIsMuted(next === 0);
              }}
              sx={{
                color: '#fff',
                padding: '12px 0',
                '& .MuiSlider-thumb': { width: 10, height: 10 },
              }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: theme.tokens.typography.monoFontFamily,
              fontSize: '11px',
              color: '#fff',
              marginInlineStart: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </Typography>

          <Box sx={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={cycleSpeed}
              aria-label={`Playback speed ${speed}x`}
              size="small"
              sx={{ ...controlBtnSx, width: 36 }}
            >
              <Typography
                sx={{
                  fontFamily: theme.tokens.typography.monoFontFamily,
                  fontSize: '11px',
                  fontWeight: 700,
                }}
              >
                {speed}x
              </Typography>
            </IconButton>

            <IconButton
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              size="small"
              sx={controlBtnSx}
            >
              {isFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoPlayer;
